/* Multi-month comparison panel — actuals vs prior month with simple SVG sparklines.
   Loaded inline at the bottom of the MgmtEditor as a "Compare months" expandable card. */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChartLineUp, TrendUp, TrendDown } from "@phosphor-icons/react";
import { C, kes, Panel } from "./shared";

export function CompareMonths({ company }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/mgmt/compare?months=12${company ? `&company=${encodeURIComponent(company)}` : ""}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load comparison"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  if (loading) {
    return <div className="text-white/45 text-sm">Loading multi-month comparison…</div>;
  }
  const months = data?.months || [];
  if (!months.length) {
    return (
      <div className="rounded-xl border p-5 text-white/55 text-sm" style={{ borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(0,0,0,0.25)" }} data-testid="mgmt-compare-empty">
        Run management accounts for at least one more month and the comparison will light up here.
      </div>
    );
  }
  const series = data.series || {};

  return (
    <div className="space-y-6" data-testid="mgmt-compare-panel">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.orange }}>
          <ChartLineUp size={12} weight="fill" /> Multi-month comparison
        </div>
        <h3 className="text-2xl font-extrabold tracking-tight mt-2" style={{ letterSpacing: "-0.02em" }}>
          {months.length} month{months.length === 1 ? "" : "s"} of <span style={{ color: C.teal }}>{company || "your business"}</span>
        </h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Sparkline label="Revenue" points={series.revenue} fmt={kes} />
        <Sparkline label="Gross profit" points={series.gross_profit} fmt={kes} />
        <Sparkline label="Operating profit" points={series.operating_profit} fmt={kes} />
        <Sparkline label="Net profit" points={series.net_profit} fmt={kes} />
        <Sparkline label="Gross margin" points={series.gross_margin} fmt={(v) => `${(v || 0).toFixed(1)}%`} />
        <Sparkline label="Operating margin" points={series.operating_margin} fmt={(v) => `${(v || 0).toFixed(1)}%`} />
        <Sparkline label="Net margin" points={series.net_margin} fmt={(v) => `${(v || 0).toFixed(1)}%`} />
        <Sparkline label="Closing cash" points={series.cash_total} fmt={kes} />
      </div>

      <Panel title="Month-by-month detail">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-white/45 uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2">Month</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Gross profit</th>
                <th className="px-3 py-2 text-right">Op profit</th>
                <th className="px-3 py-2 text-right">Net profit</th>
                <th className="px-3 py-2 text-right">Net margin</th>
                <th className="px-3 py-2 text-right">Cash</th>
                <th className="px-3 py-2 text-right">Δ Net vs prev</th>
              </tr>
            </thead>
            <tbody>
              {months.map((m, i) => {
                const v = m.variance_vs_prev?.net_profit;
                const dn = v?.delta < 0;
                return (
                  <tr key={m.account_id} className="border-t border-white/5" data-testid={`mgmt-compare-row-${m.target_month}`}>
                    <td className="px-3 py-2 font-mono-num">{m.target_month}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{kes(m.pnl.revenue_total)}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{kes(m.pnl.gross_profit)}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{kes(m.pnl.operating_profit)}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{kes(m.pnl.net_profit)}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{m.kpis?.net_margin === null ? "—" : `${m.kpis.net_margin.toFixed(1)}%`}</td>
                    <td className="px-3 py-2 text-right font-mono-num">{kes(m.cf?.closing_cash_ledger)}</td>
                    <td className="px-3 py-2 text-right">
                      {i === 0 || !v ? <span className="text-white/30">—</span> : (
                        <span className="inline-flex items-center gap-1 font-mono-num"
                          style={{ color: dn ? "#F87171" : C.teal }}>
                          {dn ? <TrendDown size={11} weight="bold" /> : <TrendUp size={11} weight="bold" />}
                          {kes(v.delta)} ({v.pct === null ? "—" : `${v.pct.toFixed(0)}%`})
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function Sparkline({ label, points, fmt }) {
  const vals = (points || []).map((p) => Number(p.v) || 0);
  const hasData = vals.some((v) => v !== 0);
  const last = vals[vals.length - 1];
  const prev = vals[vals.length - 2];
  const dn = prev !== undefined && last < prev;
  const W = 160; const H = 36;
  const min = Math.min(...vals, 0);
  const max = Math.max(...vals, 1);
  const range = (max - min) || 1;
  const stepX = vals.length > 1 ? W / (vals.length - 1) : 0;
  const path = vals.map((v, i) => `${i === 0 ? "M" : "L"} ${i * stepX} ${H - ((v - min) / range) * H}`).join(" ");

  return (
    <div className="rounded-xl border p-4" style={{ borderColor: "rgba(123, 211, 206, 0.15)", backgroundColor: C.panel }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/45">{label}</div>
      <div className="text-xl font-extrabold mt-1" style={{ color: hasData ? C.teal : "rgba(255,255,255,0.3)" }}>{fmt(last)}</div>
      {hasData ? (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full mt-2">
          <path d={path} fill="none" stroke={dn ? "#F87171" : C.teal} strokeWidth="1.6" />
        </svg>
      ) : (
        <div className="h-9 mt-2 text-[10px] text-white/30 italic flex items-center">No data</div>
      )}
    </div>
  );
}
